import { fetchPaperAgentsList, type PaperAgentMeta } from "../api/agentsPaperClient";
import { isAgenticFeatureEnabled } from "./agenticFeatures";

export type AgentsBadgeStatus = "ok" | "idle" | "checking" | "error" | "unknown";

export interface AgentsStatusResult {
  visible: boolean;
  badgeStatus: AgentsBadgeStatus;
  anyEnabled: boolean;
  hasUpdate: boolean;
  enabledCount: number;
  message: string;
}

const STATUS_POLL_MS = 60_000;
const LAST_VISIT_KEY = "sfe-agents-last-visit";

let latestResult: AgentsStatusResult = {
  visible: false,
  badgeStatus: "unknown",
  anyEnabled: false,
  hasUpdate: false,
  enabledCount: 0,
  message: "Agentic",
};

function setHoverTip(el: HTMLElement, tip: string): void {
  el.title = tip;
  el.setAttribute("data-tip", tip);
}

function readLastVisitAt(): string | null {
  try {
    const raw = localStorage.getItem(LAST_VISIT_KEY);
    return raw && Date.parse(raw) ? raw : null;
  } catch {
    return null;
  }
}

function writeLastVisitAt(iso: string): void {
  try {
    localStorage.setItem(LAST_VISIT_KEY, iso);
  } catch {
    /* ignore quota / private mode */
  }
}

function latestActionAt(agents: PaperAgentMeta[]): string | null {
  let best: string | null = null;
  let bestMs = -Infinity;
  for (const agent of agents) {
    const ts = agent.lastActionAt;
    if (typeof ts !== "string" || !ts) continue;
    const ms = Date.parse(ts);
    if (!Number.isFinite(ms) || ms <= bestMs) continue;
    bestMs = ms;
    best = ts;
  }
  return best;
}

function isAgentsTabActive(): boolean {
  if (document.body.getAttribute("data-sfe-agents-active") === "true") return true;
  return Boolean(
    document.querySelector(".sfe-mobile-tabbar [data-tab='agents'].active, .sfe-mobile-tabbar [data-tab='agents'].sfe-mobile-tab-active"),
  );
}

function hiddenAgentsStatus(): AgentsStatusResult {
  return {
    visible: false,
    badgeStatus: "unknown",
    anyEnabled: false,
    hasUpdate: false,
    enabledCount: 0,
    message: "Agentic",
  };
}

export function deriveAgentsStatus(agents: PaperAgentMeta[]): AgentsStatusResult {
  if (!agents.length) return hiddenAgentsStatus();

  const enabledCount = agents.filter((a) => a.enabled).length;
  const anyEnabled = enabledCount > 0;
  const lastActionAt = latestActionAt(agents);
  const lastVisitAt = readLastVisitAt();
  const onAgentsTab = isAgentsTabActive();
  // Only after a prior Agentic visit — "new since last visit", not all-time history.
  const hasUpdate =
    !onAgentsTab &&
    Boolean(
      lastActionAt &&
        lastVisitAt &&
        Date.parse(lastActionAt) > Date.parse(lastVisitAt),
    );

  const parts: string[] = [];
  if (anyEnabled) {
    parts.push(enabledCount === 1 ? "1 agent on" : `${enabledCount} agents on`);
  } else {
    parts.push("No agents on");
  }
  if (hasUpdate) parts.push("new activity since last visit");

  return {
    visible: true,
    badgeStatus: anyEnabled ? "ok" : "idle",
    anyEnabled,
    hasUpdate,
    enabledCount,
    message: `Agentic — ${parts.join(" · ")}`,
  };
}

export function getAgentsStatusResult(): AgentsStatusResult {
  return latestResult;
}

export function agentsStatusIndicatorHtml(opts: { label?: string } = {}): string {
  const label = opts.label ?? "AG";
  return `
    <button type="button" class="sfe-agents-status" data-action="agents-status" hidden aria-label="Agentic status" data-tip="Agentic — checking…" title="Agentic — checking…">
      <span class="sfe-agents-status-dot" data-agents-dot data-status="unknown" aria-hidden="true"></span>
      <span class="sfe-agents-status-label">${label}</span>
      <span class="sfe-agents-status-update" data-agents-update hidden aria-hidden="true"></span>
    </button>
  `;
}

export function applyAgentsStatus(host: ParentNode, result: AgentsStatusResult): void {
  latestResult = result;
  const btn = host.querySelector<HTMLElement>("[data-action='agents-status']");
  const dot = host.querySelector<HTMLElement>("[data-agents-dot]");
  const update = host.querySelector<HTMLElement>("[data-agents-update]");
  if (!btn) return;

  btn.hidden = !result.visible;
  btn.dataset.hasUpdate = result.hasUpdate ? "true" : "false";
  if (dot) dot.dataset.status = result.badgeStatus;
  if (update) update.hidden = !result.hasUpdate;
  setHoverTip(btn, result.message);
}

/** Clear unread badge — call when the Agentic tab is opened. */
export function markAgentsPageVisited(): void {
  writeLastVisitAt(new Date().toISOString());
  if (latestResult.visible) {
    latestResult = { ...latestResult, hasUpdate: false, message: latestResult.message.replace(/ · new activity since last visit$/, "") };
  }
  window.dispatchEvent(new CustomEvent("sfe:agents-page-visited"));
}

async function loadAgentsStatus(): Promise<AgentsStatusResult> {
  if (!isAgenticFeatureEnabled()) return hiddenAgentsStatus();

  try {
    const payload = await fetchPaperAgentsList();
    return deriveAgentsStatus(payload.agents || []);
  } catch (err) {
    const message = err instanceof Error ? err.message : "Could not read Agentic status";
    if (!latestResult.visible) return hiddenAgentsStatus();
    return {
      visible: true,
      badgeStatus: "error",
      anyEnabled: false,
      hasUpdate: false,
      enabledCount: 0,
      message,
    };
  }
}

export function startAgentsStatusPolling(
  onUpdate: (result: AgentsStatusResult) => void,
): () => void {
  if (!isAgenticFeatureEnabled()) {
    onUpdate(hiddenAgentsStatus());
    return () => {};
  }

  let cancelled = false;

  const run = async (opts?: { showChecking?: boolean }) => {
    if (!isAgenticFeatureEnabled()) {
      if (!cancelled) onUpdate(hiddenAgentsStatus());
      return;
    }
    if (cancelled) return;
    if (latestResult.visible && opts?.showChecking) {
      onUpdate({
        ...latestResult,
        badgeStatus: "checking",
        message: "Checking Agentic status…",
      });
    }
    const result = await loadAgentsStatus();
    if (!cancelled) onUpdate(result);
  };

  void run({ showChecking: true });
  const timer = window.setInterval(() => void run(), STATUS_POLL_MS);

  const onVisible = () => {
    if (document.visibilityState === "visible") void run();
  };
  document.addEventListener("visibilitychange", onVisible);

  const onChanged = () => void run();
  window.addEventListener("sfe:agents-status-changed", onChanged);

  const onVisited = () => {
    if (cancelled) return;
    const cleared = {
      ...latestResult,
      hasUpdate: false,
      message: latestResult.message.replace(/ · new activity since last visit$/, ""),
    };
    onUpdate(cleared);
  };
  window.addEventListener("sfe:agents-page-visited", onVisited);

  return () => {
    cancelled = true;
    window.clearInterval(timer);
    document.removeEventListener("visibilitychange", onVisible);
    window.removeEventListener("sfe:agents-status-changed", onChanged);
    window.removeEventListener("sfe:agents-page-visited", onVisited);
  };
}

export function notifyAgentsStatusChanged(): void {
  window.dispatchEvent(new CustomEvent("sfe:agents-status-changed"));
}
